// Shared IconLayer constructor for sanctions / conflicts / supply.
// Each consumer differs only in id/data/size — keeping the constructor in
// one place avoids three near-duplicate spread blocks.

import { IconLayer, type IconLayerProps } from '@deck.gl/layers';
import type { intel as i } from '@eidollon/contracts';
import { type RGBA, getMapPalette } from '../colors.js';
import { ICON_ATLAS, ICON_MAPPING, frameShapeFor } from './_shared.js';
import type { DeckLayerProps } from './registry.js';

export interface IconLayerSpecOptions {
  id: string;
  rows: readonly i.IntelSignal[];
  zoom: number;
  sizeMin: number;
  sizeMax: number;
  onPick: DeckLayerProps['onPick'];
}

export function buildIconLayer(opts: IconLayerSpecOptions): IconLayer<i.IntelSignal> {
  // Capture the palette snapshot once per layer build (deck.gl re-builds on
  // prop change). Per-row `getColor` then reads from this local closure
  // instead of calling `getMapPalette()` thousands of times per frame.
  const palette = getMapPalette();
  const ramp = (score: number): RGBA => {
    // Heat ramp boundaries — mirrors severityRamp() in colors.ts. Inlined
    // here so per-row getColor() stays a closure-only lookup.
    if (score >= 90) return palette.sevHot;
    if (score >= 70) return palette.sevElevated;
    if (score >= 40) return palette.sevWatch;
    return palette.sevOk;
  };
  // Per-row size: a zoom-derived base, scaled up by severity so a small
  // smoulder and a megafire (or an M3 and an M8 quake) are visually distinct
  // and don't merge into one uniform blob at country zoom. Severity 0 sits at
  // the base; severity 100 reaches the layer's sizeMax. Clamped to
  // [sizeMin, sizeMax] so the ramp never blows past the layer's budget.
  const base = Math.min(opts.sizeMax, opts.sizeMin + opts.zoom);
  const span = Math.max(0, opts.sizeMax - base);
  const sizeFor = (s: i.IntelSignal): number => {
    const sev = Math.max(0, Math.min(100, s.severity ?? 0));
    return Math.min(opts.sizeMax, base + (span * sev) / 100);
  };
  const props: IconLayerProps<i.IntelSignal> = {
    id: opts.id,
    data: opts.rows as i.IntelSignal[],
    pickable: true,
    iconAtlas: ICON_ATLAS,
    iconMapping: ICON_MAPPING,
    sizeUnits: 'pixels',
    sizeMinPixels: opts.sizeMin,
    sizeMaxPixels: opts.sizeMax,
    // biome-ignore lint/style/noNonNullAssertion: callers pre-filter via withGeom().
    getPosition: (s) => [s.geom!.lng, s.geom!.lat],
    getIcon: (s) => frameShapeFor(s),
    getSize: sizeFor,
    getColor: (s) => ramp(s.severity),
    updateTriggers: { getSize: opts.zoom },
  };
  if (opts.onPick) {
    const cb = opts.onPick;
    props.onClick = (info) => {
      cb((info.object as i.IntelSignal | null) ?? null);
      return true;
    };
  }
  return new IconLayer<i.IntelSignal>(props);
}
